import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CreateAd from "./pages/CreateAd";
import EditAd from "./pages/EditAd";
import AdminSettings from "./pages/AdminSettings";
import AdDetail from "./pages/AdDetail";
import { SiteBrandingProvider } from "./contexts/SiteBrandingContext";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const processAuth = async () => {
      // Check for session_id in URL fragment (Google OAuth callback)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          // Exchange session_id for session data
          const response = await axios.get(
            process.env.REACT_APP_AUTH_API_URL,
            {
              headers: {
                'X-Session-ID': sessionId
              }
            }
          );

          const { email, name, session_token } = response.data;

          // Store session token as JWT token
          localStorage.setItem("token", session_token);
          localStorage.setItem("user", JSON.stringify({ email, name }));
          
          setIsAuthenticated(true);
          setUser({ email, name });

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error("Google auth failed:", error);
        }
      } else {
        // Check existing auth
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");
        
        if (token && storedUser) {
          setIsAuthenticated(true);
          setUser(JSON.parse(storedUser));
        }
      }
      
      setLoading(false);
    };

    processAuth();
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setUser(null);
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" /> : <Login onLogin={handleLogin} />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/" /> : <Register onLogin={handleLogin} />
      } />
      <Route path="/" element={
        <Dashboard isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
      } />
      <Route path="/ad/:adId" element={
        <AdDetail isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
      } />
      <Route path="/create-ad" element={
        isAuthenticated && user?.email === process.env.REACT_APP_ADMIN_EMAIL ? 
          <CreateAd user={user} onLogout={handleLogout} /> : 
          <Navigate to="/" />
      } />
      <Route path="/edit-ad/:adId" element={
        isAuthenticated && user?.email === process.env.REACT_APP_ADMIN_EMAIL ? 
          <EditAd user={user} onLogout={handleLogout} /> : 
          <Navigate to="/" />
      } />
      <Route path="/admin/settings" element={
        isAuthenticated && user?.email === process.env.REACT_APP_ADMIN_EMAIL ? 
          <AdminSettings user={user} onLogout={handleLogout} /> : 
          <Navigate to="/" />
      } />
    </Routes>
  );
}

function App() {
  return (
    <SiteBrandingProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </SiteBrandingProvider>
  );
}

export default App;